import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gamepad2, ShieldCheck, Clock, Coins, Info, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

export default function PlayEarn() {
  const [config, setConfig] = useState(null);
  const [adgem, setAdgem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/spawntap/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false })).finally(() => setLoading(false));
    api.get("/adgem/config").then((r) => setAdgem(r.data)).catch(() => setAdgem({ enabled: false }));
  }, []);

  return (
    <div className="space-y-6" data-testid="play-earn-page">
      <div>
        <p className="eyebrow mb-1">/ SPAWNTAP OFFERWALL</p>
        <h1 className="font-display font-black text-2xl sm:text-3xl">Play & Earn</h1>
        <p className="text-slate-400 text-sm mt-1">
          Play games and complete premium offers from the Spawntap network to earn coins.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {[
          { icon: ShieldCheck, t: "Verified rewards", d: "Every conversion is signature-validated on our server.", c: "text-cyan-neon" },
          { icon: Clock, t: "Admin approved", d: "Rewards stay pending until an admin confirms them.", c: "text-amber-neon" },
          { icon: Coins, t: "Auto-tracked", d: "Earnings are tied to your unique account ID.", c: "text-lime-neon" },
        ].map((x) => (
          <div key={x.t} className="neon-card p-4 flex gap-3">
            <x.icon className={`w-5 h-5 shrink-0 ${x.c}`} />
            <div>
              <p className="font-semibold text-sm">{x.t}</p>
              <p className="text-xs text-slate-400 mt-0.5">{x.d}</p>
            </div>
          </div>
        ))}
      </div>

      {adgem?.enabled && adgem?.direct_link && (
        <div className="neon-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="adgem-section">
          <div className="flex-1">
            <p className="eyebrow mb-1">/ ADGEM OFFERS</p>
            <h3 className="font-display font-bold text-lg">AdGem Premium Offerwall</h3>
            <p className="text-slate-400 text-sm mt-1">Complete AdGem offers to earn coins. Rewards are credited automatically once AdGem verifies your completion server-side.</p>
          </div>
          <button
            onClick={() => window.open(adgem.direct_link, "_blank", "noopener")}
            data-testid="open-adgem-offerwall"
            className="btn-neon h-11 px-5 rounded-md flex items-center gap-2 shrink-0"
          >
            <ExternalLink className="w-4 h-4" /> Open AdGem Offers
          </button>
        </div>
      )}

      <div className="neon-card overflow-hidden" data-testid="spawntap-offerwall-container">
        {loading ? (
          <div className="h-[560px] flex items-center justify-center">
            <div className="flex items-center gap-3 text-cyan-neon">
              <span className="w-3 h-3 rounded-full bg-cyan-400 radar-dot" />
              <span className="font-mono-num text-sm tracking-widest">LOADING OFFERWALL…</span>
            </div>
          </div>
        ) : config?.enabled && config?.iframe_url ? (
          <div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <p className="text-xs text-slate-500 font-mono-num">POWERED BY SPAWNTAP</p>
              <button
                onClick={() => window.open(config.iframe_url, "_blank", "noopener")}
                data-testid="open-offerwall-newtab"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-cyan-500/30 text-cyan-neon hover:bg-cyan-400/10 transition"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
              </button>
            </div>
            <motion.iframe
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              src={config.iframe_url}
              title="Spawntap Offerwall"
              data-testid="spawntap-iframe"
              className="w-full h-[70vh] min-h-[520px] bg-[#07090E]"
              frameBorder="0"
              scrolling="yes"
            />
          </div>
        ) : (
          <div className="h-[420px] flex flex-col items-center justify-center text-center px-6" data-testid="spawntap-disabled">
            <Gamepad2 className="w-12 h-12 text-slate-600" />
            <h3 className="font-display font-bold text-lg mt-4">Offerwall not configured yet</h3>
            <p className="text-slate-400 text-sm mt-2 max-w-md">
              An admin needs to add the Spawntap <strong>SDK Key</strong> and S2S Key before the offerwall goes live. No offers are shown until it is configured.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-xs text-slate-500">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>Rewards from completed offers are credited after admin approval, usually within 24 hours. Clicks and test events are never rewarded.</p>
      </div>
    </div>
  );
}
